/**
 * The reader frame's plumbing that needs no Angular and no layout, so it is tested on its own
 * (jsdom loads no frame documents). Nodes of the frame's document belong to the frame's realm, so
 * nothing here uses `instanceof` against the app's DOM classes.
 */

import { internalOrigins, isInternalUrl } from '@modules/common/utils/internal-url';
import { LAZY_IMAGE_ATTRIBUTE } from '../../utils/email-document';

/** Beyond this the frame scrolls inside rather than growing the reader. */
export const MAX_FRAME_HEIGHT = 60_000;
/** Below this a shrink is layout jitter, not content. A frame grows at once. */
export const FRAME_HEIGHT_HYSTERESIS = 4;
/**
 * Added to the rounded-up content height. Content heights are fractional (Retina: 5474.1px) and a
 * frame height is whole; a frame even 0.1px short scrolls, and macOS draws a second scrollbar.
 */
export const FRAME_HEIGHT_SLACK = 1;

/**
 * Raster types only. A `blob:` URL carries the app's origin, so an SVG (which may hold script) opened
 * from it in a new tab would run as the app; such an inline image shows the blocked placeholder.
 */
const INLINE_IMAGE_TYPES: ReadonlySet<string> = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
  'image/avif',
  'image/bmp',
  'image/x-icon',
  'image/vnd.microsoft.icon',
]);

/**
 * Whether a frame's document is a built mail (its CSP first in `<head>`), not the frame's initial
 * `about:blank`, whose own load event may arrive after the reader starts listening.
 */
export function isBuiltEmailDocument(frameDocument: Document): boolean {
  return (
    frameDocument.head?.firstElementChild?.getAttribute('http-equiv')?.toLowerCase() === 'content-security-policy'
  );
}

/** The frame height to apply for a measured content height, or `null` when the change is jitter. */
export function nextFrameHeight(measured: number, current: number): number | null {
  const next = Math.min(Math.ceil(Math.max(measured, 0)) + FRAME_HEIGHT_SLACK, MAX_FRAME_HEIGHT);
  // Never shorter than the rounded-up content: the hysteresis only ever delays a shrink.
  if (next > current) {
    return next;
  }
  return current - next >= FRAME_HEIGHT_HYSTERESIS ? next : null;
}

/** Content taller than the cap: the frame stays at its maximum and must scroll itself, or the rest is unreachable. */
export function frameNeedsInnerScroll(measured: number): boolean {
  return measured > MAX_FRAME_HEIGHT;
}

/**
 * The frame's content height. Not `documentElement.scrollHeight`, which never reports less than the
 * frame's own height, so a shorter next message would keep the previous one's size.
 */
export function measureFrameContent(frameDocument: Document): number {
  // Fractional, unlike offsetHeight; body.scrollHeight still catches content overflowing the root's box.
  return Math.max(frameDocument.documentElement.getBoundingClientRect().height, frameDocument.body?.scrollHeight ?? 0);
}

/**
 * The same mouse event, as the app's document would have seen it on the iframe element: coordinates
 * moved from the frame's viewport into the app's.
 */
export function forwardedMouseEventInit(event: MouseEvent, frame: HTMLIFrameElement): MouseEventInit {
  const rectangle = frame.getBoundingClientRect();
  const left = rectangle.left + frame.clientLeft;
  const top = rectangle.top + frame.clientTop;
  return {
    bubbles: true,
    cancelable: true,
    composed: true,
    detail: event.detail,
    clientX: event.clientX + left,
    clientY: event.clientY + top,
    screenX: event.screenX,
    screenY: event.screenY,
    button: event.button,
    buttons: event.buttons,
    ctrlKey: event.ctrlKey,
    shiftKey: event.shiftKey,
    altKey: event.altKey,
    metaKey: event.metaKey,
  };
}

/**
 * The same key event for the app's document, on the iframe element: Escape closing a modal, list
 * shortcuts. The original is left alone, so copy and select-all inside the mail keep working.
 */
export function forwardedKeyboardEventInit(event: KeyboardEvent): KeyboardEventInit {
  return {
    bubbles: true,
    cancelable: true,
    composed: true,
    key: event.key,
    code: event.code,
    location: event.location,
    repeat: event.repeat,
    isComposing: event.isComposing,
    ctrlKey: event.ctrlKey,
    shiftKey: event.shiftKey,
    altKey: event.altKey,
    metaKey: event.metaKey,
  };
}

/** How far outside the visible area a held-back image starts loading: about one viewport. */
export const LAZY_IMAGE_ROOT_MARGIN = '100% 0px';

/**
 * `rootMargin` grows only the root (the top-level viewport); the reader's scroll container clips
 * first, so without `scrollMargin` an image would load only once actually visible. Browsers without
 * `scrollMargin` ignore it and fall back to exactly that.
 */
export const LAZY_IMAGE_OBSERVER_INIT: IntersectionObserverInit & { readonly scrollMargin: string } = {
  rootMargin: LAZY_IMAGE_ROOT_MARGIN,
  scrollMargin: LAZY_IMAGE_ROOT_MARGIN,
};

/**
 * Assigns each held-back remote image its source once it comes within about one viewport of the
 * visible area. The observer is the app's, with the implicit root (the top-level viewport), which
 * reaches elements in a same-origin frame — the frame itself is full height, so its own viewport
 * would call every image visible. Without IntersectionObserver every image loads. Returns the teardown.
 */
export function observeLazyImages(
  frameDocument: Document,
  sources: ReadonlyArray<string>,
  Observer: typeof IntersectionObserver | undefined = globalThis.IntersectionObserver,
): () => void {
  const images = Array.from(frameDocument.querySelectorAll(`img[${LAZY_IMAGE_ATTRIBUTE}]`));
  const assign = (image: Element): void => {
    const source = sources[Number.parseInt(image.getAttribute(LAZY_IMAGE_ATTRIBUTE) ?? '', 10)];
    image.removeAttribute(LAZY_IMAGE_ATTRIBUTE);
    if (source) {
      image.setAttribute('src', source);
    }
  };
  if (images.length === 0) {
    return () => undefined;
  }
  if (!Observer) {
    images.forEach(assign);
    return () => undefined;
  }
  const observer = new Observer(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          assign(entry.target);
        }
      }
    },
    LAZY_IMAGE_OBSERVER_INIT,
  );
  images.forEach((image) => observer.observe(image));
  return () => observer.disconnect();
}

/** An inline attachment on our gateway, as mail-app rewrites `cid:` images: `/api/files/content/<checksum>`. */
const INLINE_CONTENT_PATH = /^\/api\/files\/content\/([A-Za-z0-9_-]+)\/?$/;

/**
 * The workspace to name when signing a message's inline images: the tenant that owns the message, unless
 * it is the signed-in account's own, which the gateway already resolves when no `space_id` is sent.
 * mail-app stores the images under the message's tenant and writes their URLs without a `space_id`, so a
 * team or member mailbox's images are only found when the request names that tenant — the same
 * `space_id=<accountId>` an attachment download sends.
 */
export function inlineImageTenant(messageTenant: string | null | undefined, ownAccountId: string | null): string | null {
  return messageTenant && messageTenant !== ownAccountId ? messageTenant : null;
}

/**
 * The gateway's signed-URL endpoint (`…/api/files/content/<checksum>/url`) for an inline image, or
 * `null` unless the image is exactly an inline attachment on one of our origins (exact origin match).
 * `tenantId` (see {@link inlineImageTenant}) is sent as `space_id`.
 */
export function inlineImageSignedUrlEndpoint(
  source: string,
  origins: ReadonlyArray<string> = internalOrigins(),
  tenantId: string | null = null,
): string | null {
  if (!isInternalUrl(source, origins)) {
    return null;
  }
  const url = new URL(source, window.location.href);
  const checksum = INLINE_CONTENT_PATH.exec(url.pathname)?.[1];
  if (!checksum) {
    return null;
  }
  if (tenantId) {
    url.searchParams.set('space_id', tenantId);
  }
  return `${url.origin}/api/files/content/${checksum}/url${url.search}`;
}

/** The `signedUrl` of the endpoint's JSON answer, if it is an http(s) URL. */
export function signedImageUrl(body: unknown): string | null {
  if (typeof body !== 'object' || body === null || !('signedUrl' in body) || typeof body.signedUrl !== 'string') {
    return null;
  }
  try {
    const url = new URL(body.signedUrl);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch {
    return null;
  }
}

/** At most this many distinct own-origin images are fetched per message; the rest wait for consent. */
export const MAX_INLINE_IMAGES = 24;
/** How many of them are fetched at once. */
export const INLINE_IMAGE_CONCURRENCY = 4;

/**
 * One own-origin image into a raster `blob:` URL, or `null`. An inline attachment goes through the
 * gateway's signed-URL endpoint (the API may be on another origin than the app): the endpoint with the
 * session cookie, then the object store's signed URL without credentials. Any other image on our
 * origins (an app asset) is fetched directly with the session.
 */
async function fetchInlineImage(
  source: string,
  signal: AbortSignal,
  tenantId: string | null,
  fetchImage: typeof fetch,
  origins: ReadonlyArray<string>,
): Promise<string | null> {
  let imageUrl: string;
  let credentials: RequestCredentials;
  const endpoint = inlineImageSignedUrlEndpoint(source, origins, tenantId);
  if (endpoint) {
    const answer = await fetchImage(endpoint, { credentials: 'include', signal });
    const signedUrl = answer.ok ? signedImageUrl(await answer.json()) : null;
    if (!signedUrl) {
      return null;
    }
    imageUrl = signedUrl;
    credentials = 'omit';
  } else if (isInternalUrl(source, origins)) {
    imageUrl = new URL(source, window.location.href).href;
    credentials = 'include';
  } else {
    return null;
  }
  const response = await fetchImage(imageUrl, { credentials, referrerPolicy: 'no-referrer', signal });
  if (!response.ok) {
    return null;
  }
  const blob = await response.blob();
  const type = blob.type.split(';')[0].trim().toLowerCase();
  if (!INLINE_IMAGE_TYPES.has(type) || signal.aborted) {
    return null;
  }
  return URL.createObjectURL(new Blob([blob], { type }));
}

/**
 * Fetches the mail's own-origin images (already filtered by the builder) into `blob:` URLs for the
 * reader's frame, whose policy allows no other image before consent: the first
 * {@link MAX_INLINE_IMAGES}, {@link INLINE_IMAGE_CONCURRENCY} at a time, stopping when `signal` aborts.
 * A source missing from the result (failed, non-raster, past the cap or aborted) is the caller's to
 * treat as remote content. The caller owns (and revokes) every URL returned. `tenantId` is the
 * message's workspace from {@link inlineImageTenant}.
 */
export async function prefetchInlineImages(
  sources: ReadonlyArray<string>,
  signal: AbortSignal,
  tenantId: string | null = null,
  fetchImage: typeof fetch = (input, init) => fetch(input, init),
  origins: ReadonlyArray<string> = internalOrigins(),
): Promise<Map<string, string>> {
  const urls = new Map<string, string>();
  const queue = Array.from(new Set(sources)).slice(0, MAX_INLINE_IMAGES);
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < queue.length && !signal.aborted) {
      const source = queue[next++];
      try {
        const url = await fetchInlineImage(source, signal, tenantId, fetchImage, origins);
        if (url) {
          urls.set(source, url);
        }
      } catch {
        // Unreachable, unauthorized or aborted: the caller treats it as remote content.
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(INLINE_IMAGE_CONCURRENCY, queue.length) }, worker));
  return urls;
}

/** Revokes every `blob:` URL in `urls`. */
export function revokeObjectUrls(urls: Iterable<string>): void {
  for (const url of urls) {
    URL.revokeObjectURL(url);
  }
}
